import { DATASET_FOR_UI_CONFIG_REFRESH_ON_OPTIMIZE, useDatasets } from 'api/dataset/dataset.api'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { EditTimeSeriesProps } from 'ui/uiConfig/anchorComponents/EditTimeSeries/EditTimeSeries'
import Datetime from 'utils/datetime/datetime'
import { calculateDataQuality, getTimeRange } from './badDataBanner.util'

const OFFSET_START_TIME_HOURS = -4 // Check only the last 4 hours of data to avoid showing warnings all the time. It's most important that we have fresh data.

type BadDataBannerProps = {
  isVisible: boolean
}

export default function BadDataBanner({ isVisible }: BadDataBannerProps): ReactElement | null {
  if (!isVisible) {
    return null
  }

  return <BadDataBannerContent />
}

function BadDataBannerContent(): ReactElement | null {
  const { data: editTimeSeriesUiConfig } = useUiConfigAnchorComponent('edit_time_series')
  const { hasAccess } = useAuth()
  const hasEditTimeSeriesAccess = hasAccess({ module: 'edittimeseries' })
  const { t } = useTranslation()

  const datasetInstructions = useMemo(() => {
    if (!hasEditTimeSeriesAccess) {
      return []
    }
    return editTimeSeriesUiConfig?.dataset_instructions || []
  }, [editTimeSeriesUiConfig, hasEditTimeSeriesAccess])

  const { startTime, endTime, datasetMinHorizonOffsets, currentTime } = useMemo(() => {
    const props = editTimeSeriesUiConfig?.props as EditTimeSeriesProps | undefined
    const items = props?.items || []

    // Build mapping of dataset return_id to min_horizon_hour_offset
    const datasetMinHorizonOffsets: Record<string, number> = {}
    let maxHorizonOffset = -1 // Default for historical data (up to current hour minus 1)

    items.forEach((item) => {
      const dataId = String(item.data_id)
      if (item.min_horizon_hour_offset !== undefined) {
        datasetMinHorizonOffsets[dataId] = item.min_horizon_hour_offset
        // Track the furthest horizon for determining endTime
        if (item.min_horizon_hour_offset > maxHorizonOffset) {
          maxHorizonOffset = item.min_horizon_hour_offset
        }
      }
    })

    const { startTime, endTime, currentTime } = getTimeRange({
      offsetStartTimeInHours: OFFSET_START_TIME_HOURS, // Check only the last 4 hours of data to avoid showing warnings all the time. It's most important that we have fresh data.
      offsetEndTimeInHours: maxHorizonOffset, // Use the furthest horizon offset, or -1 for historical data
      minutesAfterHourThreshold: 10, // We want to have some slack on new data and not show warning if the current time is too close to the current hour.
    })

    return { startTime, endTime, datasetMinHorizonOffsets, currentTime }
  }, [editTimeSeriesUiConfig])

  const { data: datasets = [] } = useDatasets(
    editTimeSeriesUiConfig?.id ?? -1,
    editTimeSeriesUiConfig?.version ?? -1,
    datasetInstructions,
    startTime,
    endTime,
    {
      ignoreZoom: true,
      datasetRefreshToken: DATASET_FOR_UI_CONFIG_REFRESH_ON_OPTIMIZE, // The input data is dependent on optimization and meas-values which are refreshed with this token. This keeps the banner in sync with the optimization charts.
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
    }
  )

  const { historicMissing, futureMissing, historicExpected } = useMemo(() => {
    const { historicMissing, futureMissing, historicExpected } = calculateDataQuality({
      datasets,
      startTime,
      datasetMinHorizonOffsets,
      currentTime,
    })

    return {
      historicMissing,
      futureMissing,
      historicExpected,
    }
  }, [datasets, startTime, datasetMinHorizonOffsets, currentTime])

  if (!hasEditTimeSeriesAccess) {
    return null
  }

  if (!editTimeSeriesUiConfig) {
    return null
  }

  if (!datasets?.length) {
    return null
  }

  if (historicMissing + futureMissing === 0) {
    return null
  }

  let errorMessageLine1: string | null = null
  let errorMessageLine2: string | null = null

  if (historicMissing > 0) {
    errorMessageLine1 = t(
      '{{count}} data points ({{percentageOfBadHistoricDataPoints}}%) missing for the last {{nrOfHistoricHours}} hours. Optimization results and forecast accuracy may be affected.',
      {
        count: historicMissing,
        percentageOfBadHistoricDataPoints: ((historicMissing / historicExpected) * 100).toFixed(1),
        nrOfHistoricHours: Math.abs(OFFSET_START_TIME_HOURS),
      }
    )
  }

  if (futureMissing > 0) {
    const futureDataPointsWarning = t('{{count}} forecast values missing. Optimization results may be affected.', {
      count: futureMissing,
    })
    if (errorMessageLine1 === null) {
      errorMessageLine1 = futureDataPointsWarning
    } else {
      errorMessageLine2 = futureDataPointsWarning
    }
  }

  errorMessageLine1 = t('Warning') + '!' + ' ' + errorMessageLine1

  const startTimeQueryParam = Datetime.toLocalTime(startTime, 'standard')
  const endTimeQueryParam = Datetime.toLocalTime(endTime, 'standard')
  const url = `/edittimeseries?startTime=${startTimeQueryParam}&endTime=${endTimeQueryParam}`

  const bannerText = errorMessageLine2 ? `${errorMessageLine1}\n${errorMessageLine2}` : errorMessageLine1

  return (
    <div className="px-4 mt-1">
      <InfoBanner
        style="warning"
        text={bannerText}
        url={{
          link: url,
          text: t('Inspect Data'),
        }}
      />
    </div>
  )
}
